import { useState } from 'react';
import clsx from 'clsx';
import { AssetTypesEnum, LenderDocumentTypeEnum, SelectOption } from '@types';
import { ReactComponent as SearchIcon } from '@assets/icons/search_icon.svg';
import {
  getDocumentSelectionGroups,
  removeOrphanedIdentificationDocuments
} from './documentSelection';

type Props = {
  assetType: AssetTypesEnum;
  options: SelectOption[];
  selectedDocuments: LenderDocumentTypeEnum[];
  onChange: (documents: LenderDocumentTypeEnum[]) => void;
};

const uniqueDocuments = (
  documents: LenderDocumentTypeEnum[]
): LenderDocumentTypeEnum[] => Array.from(new Set(documents));

const DocumentToggle = ({
  option,
  checked,
  onToggle
}: {
  option: SelectOption;
  checked: boolean;
  onToggle: () => void;
}) => (
  <div
    className="-mx-4 flex cursor-pointer items-center justify-between gap-4 px-4 py-2 hover:bg-grey-50"
    onClick={onToggle}>
    <span className="text-sm text-grey-700">{option.label}</span>
    <button
      type="button"
      role="switch"
      aria-label={option.label}
      aria-checked={checked}
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      className={clsx(
        'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2',
        checked ? 'bg-secondary' : 'bg-grey-200'
      )}>
      <span
        aria-hidden="true"
        className={clsx(
          'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          checked ? 'translate-x-4' : 'translate-x-0'
        )}
      />
    </button>
  </div>
);

const DocumentVerificationSelector = ({
  assetType,
  options,
  selectedDocuments,
  onChange
}: Props) => {
  const [searchTerm, setSearchTerm] = useState('');
  const groups = getDocumentSelectionGroups(assetType, options);
  const selectedSet = new Set(selectedDocuments);
  const selectedCount = options.filter((option) =>
    selectedSet.has(option.value as LenderDocumentTypeEnum)
  ).length;
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredGroups = groups
    .map((group) => ({
      ...group,
      filteredOptions: group.options.filter((option) =>
        option.label.toLowerCase().includes(normalizedSearchTerm)
      )
    }))
    .filter((group) => group.filteredOptions.length > 0);

  const updateDocuments = (documents: LenderDocumentTypeEnum[]) => {
    onChange(removeOrphanedIdentificationDocuments(documents));
  };

  const toggleDocument = (document: LenderDocumentTypeEnum) => {
    updateDocuments(
      selectedSet.has(document)
        ? selectedDocuments.filter((item) => item !== document)
        : uniqueDocuments([...selectedDocuments, document])
    );
  };

  const toggleGroup = (groupOptions: SelectOption[]) => {
    const groupValues = groupOptions.map(
      (option) => option.value as LenderDocumentTypeEnum
    );
    const allSelected = groupValues.every((value) => selectedSet.has(value));

    updateDocuments(
      allSelected
        ? selectedDocuments.filter((item) => !groupValues.includes(item))
        : uniqueDocuments([...selectedDocuments, ...groupValues])
    );
  };

  return (
    <div className="rounded-lg border border-grey-200 bg-white">
      <div className="sticky top-0 z-10 flex flex-col gap-3 border-b border-grey-200 bg-white px-4 py-3 md:flex-row md:items-center">
        <div className="relative min-w-0 flex-1">
          <SearchIcon className="absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-grey-400" />
          <input
            type="search"
            aria-label="Search documents"
            placeholder="Search documents"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="w-full rounded-md border border-grey-300 py-2 pl-9 pr-3 text-sm text-grey-900 placeholder:text-grey-400 focus:border-secondary focus:outline-none"
          />
        </div>
        <div className="flex flex-shrink-0 items-center justify-between gap-4 md:justify-end">
          <span className="text-[12px] font-semibold text-grey-700">
            {selectedCount}{' '}
            {selectedCount === 1 ? 'document selected' : 'documents selected'}
          </span>
          <button
            type="button"
            disabled={selectedCount === 0}
            onClick={() => updateDocuments([])}
            className="rounded border border-grey-300 px-3 py-1.5 text-sm font-medium text-grey-500 hover:bg-grey-50 disabled:cursor-not-allowed disabled:opacity-50">
            Clear all
          </button>
        </div>
      </div>

      {filteredGroups.map((group) => {
        const selectedInGroup = group.options.filter((option) =>
          selectedSet.has(option.value as LenderDocumentTypeEnum)
        ).length;
        const allSelected = group.filteredOptions.every((option) =>
          selectedSet.has(option.value as LenderDocumentTypeEnum)
        );

        return (
          <section
            key={group.id}
            aria-labelledby={`${group.id}-heading`}
            className="border-b border-grey-200 px-4 py-4 last:border-b-0">
            <div className="mb-2 flex items-center justify-between gap-4">
              <h3
                id={`${group.id}-heading`}
                className="text-xs font-bold uppercase tracking-wide text-secondary">
                {group.label}{' '}
                <span className="font-normal normal-case text-grey-400 pl-2">
                  {selectedInGroup} of {group.options.length}
                </span>
              </h3>
              <button
                type="button"
                onClick={() => toggleGroup(group.filteredOptions)}
                className="flex-shrink-0 text-sm font-semibold text-secondary hover:text-secondary-dark hover:underline">
                {allSelected ? 'Turn all off' : 'Turn all on'}
              </button>
            </div>

            <div>
              {group.filteredOptions.map((option) => {
                const value = option.value as LenderDocumentTypeEnum;
                return (
                  <DocumentToggle
                    key={option.value}
                    option={option}
                    checked={selectedSet.has(value)}
                    onToggle={() => toggleDocument(value)}
                  />
                );
              })}
            </div>
          </section>
        );
      })}
      {filteredGroups.length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-grey-500">
          No documents found.
        </p>
      )}
    </div>
  );
};

export default DocumentVerificationSelector;
